/**
 * Single-flight concurrency lock for the media studio dialog.
 *
 * Why a hook instead of a context: the lock is dialog-scoped — each dialog
 * instance owns its own in-flight operation. ImageBody mounts the hook and
 * threads `isOperating` + `runWithId` through props so chips, action-bar
 * buttons, and inline popovers all share the same lock.
 *
 * `run` and `runWithId` reject silently (return undefined) when an operation
 * is already in flight — toast + error UX is the caller's responsibility.
 */
import { useCallback, useRef, useState } from 'react';

export interface MediaStudioOperationHandle {
  isOperating: boolean;
  run: <T>(fn: () => Promise<T>) => Promise<T | undefined>;
  activeId: string | null;
  runWithId: <T>(id: string, fn: () => Promise<T>) => Promise<T | undefined>;
}

export function useMediaStudioOperation(): MediaStudioOperationHandle {
  const [isOperating, setIsOperating] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const inFlightRef = useRef(false);

  const runWithId = useCallback(
    async <T>(id: string, fn: () => Promise<T>): Promise<T | undefined> => {
      if (inFlightRef.current) return undefined;
      inFlightRef.current = true;
      setIsOperating(true);
      setActiveId(id);
      try {
        return await fn();
      } finally {
        inFlightRef.current = false;
        setIsOperating(false);
        setActiveId(null);
      }
    },
    [],
  );

  const run = useCallback(async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
    if (inFlightRef.current) return undefined;
    inFlightRef.current = true;
    setIsOperating(true);
    try {
      return await fn();
    } finally {
      inFlightRef.current = false;
      setIsOperating(false);
      setActiveId(null);
    }
  }, []);

  return { isOperating, run, activeId, runWithId };
}
